import { Component, computed, EventEmitter, inject, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { AgentsStore } from '@app/store/agents.store';
import { MyAppsStore } from '@app/store/myapps.store';
import { SpacesStore } from '@app/store/spaces.store';
import WorkspaceStore from '@app/store/workspace.store';
import { BaseCompany, Company } from '@interfaces/company';
import { Role, User } from '@interfaces/user';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { CreateWorkspaceModalComponent } from '@pages/workspace/settings/create-workspace-modal/create-workspace-modal.component';
import { DeviceTypeService } from '@services/device-type.service';
import { NavigationService } from '@services/navigation.service';
import { UserService } from '@services/user.service';
import { WebsocketService } from '@services/websocket.service';
import { getTranslation } from '@shared/translate-helper';
import { NgxSpinnerService } from 'ngx-spinner';
import { MessageService } from 'primeng/api';
import { Popover } from 'primeng/popover';

/**
 * Workspace switcher rendered as a pill in the global header.
 *
 * Desktop/tablet open the list in an anchored popover, mobile in a bottom
 * sheet — both from the same `#panel` template.
 */
@Component({
  standalone: false,
  selector: 'app-manage-workspace-popup',
  templateUrl: './manage-workspace-popup.component.html',
  styleUrls: ['./manage-workspace-popup.component.scss'],
})
export class ManageWorkspacePopupComponent {
  @Output() workspaceSwitched = new EventEmitter<void>();
  /**
   * `pill` is the dark trigger in the global header; `row` is the light
   * full-width row used inside the mobile burger drawer. Both open the same
   * panel.
   */
  @Input() variant: 'pill' | 'row' = 'pill';
  public modalRef!: any;

  public readonly userData = computed(() => this.userService.getUserData());
  public currentCompany = computed(() => this.userData()?.company);
  public companiesList = computed(() => this.userData()?.companies || []);
  public canManageWorkspace = computed(() => this.userData()?.role !== Role.User);

  public readonly myAppsStore: InstanceType<typeof MyAppsStore> = inject(MyAppsStore);
  public readonly spacesStore: InstanceType<typeof SpacesStore> = inject(SpacesStore);
  public readonly workspaceStore: InstanceType<typeof WorkspaceStore> = inject(WorkspaceStore);
  public readonly agentsStore: InstanceType<typeof AgentsStore> = inject(AgentsStore);

  @ViewChild(Popover) workspacePopup!: Popover;
  @ViewChild('panel') panel!: TemplateRef<unknown>;

  constructor(
    private navigationService: NavigationService,
    private modalService: NgbModal,
    public deviceType: DeviceTypeService,
    private readonly spinner: NgxSpinnerService,
    private readonly messageService: MessageService,
    private translate: TranslateService,
    private websocketService: WebsocketService,
    private userService: UserService,
  ) {}

  public toggle(event: Event): void {
    this.workspacePopup.toggle(event);
  }

  private closePanel(): void {
    this.workspacePopup?.hide();
  }

  public openCreateWorkspaceDialog(): void {
    this.closePanel();
    this.modalRef = this.modalService.open(CreateWorkspaceModalComponent, {
      backdrop: 'static',
      windowClass:
        this.deviceType.isDesktop || this.deviceType.isTablet
          ? 'create-workspace-modal'
          : 'create-workspace-modal-mobile',
      animation: false,
      centered: true,
      fullscreen: false,
    });
  }

  public switchWorkspace(company: BaseCompany | Company) {
    const userData = {
      email: this.userData()?.email,
      company: {
        uuid: company.uuid,
      },
    } as Partial<User>;
    this.spinner.show();
    this.workspaceStore
      .switchWorkspace(userData)
      .subscribe({
        next: () => {
          const isSubscriptionPaused = this.userService.companySubscription()?.pause_collection !== null;
          this.closePanel();
          this.refreshStateAfterWorkspaceSwitch();
          this.messageService.clear();
          this.websocketService.closeWebSocket();
          this.websocketService.initializeWebSocket();
          getTranslation(this.translate, ['switch_workspace_success', 'switch_workspace_description']).subscribe(
            (res: any) => {
              this.messageService.add({
                severity: 'info',
                summary: res['switch_workspace_success'],
                detail: res['switch_workspace_description']?.replace('{{workspaceName}}', company.name),
                life: 3000,
              });
            },
          );
          this.workspaceSwitched.emit();
          if (!isSubscriptionPaused) {
            this.navigationService.navigateToSpaces();
          } else {
            this.navigationService.navigateToSubscription();
          }
        },
        error: (err) => {
          getTranslation(this.translate, 'switch_workspace_error').subscribe((res: string) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: res,
              life: 3000,
            });
          });
        },
      })
      .add(() => {
        this.spinner.hide();
      });
  }

  public getInitials(name: string): string {
    if (!name) return '';
    const words = name
      .split(' ')
      .filter((word) => word.length > 0)
      .slice(0, 2);
    const initials = words.map((word) => word.charAt(0).toUpperCase()).join('');
    return initials;
  }

  public navigateToWorkspaceManagement(): void {
    if (!this.canManageWorkspace()) {
      return;
    }
    this.navigationService.navigateToWorkspace();
    this.closePanel();
  }

  private refreshStateAfterWorkspaceSwitch(): void {
    const userData = this.userService.getUserData();
    this.spacesStore.clearStore();
    this.spacesStore.loadSpaces(true);
    this.myAppsStore.loadActiveConnectors(true);
    this.agentsStore.clearStore();
    if ([Role.ClientAdmin, Role.Admin].includes(userData?.role as Role)) {
      this.workspaceStore.initializeCompany();
      this.workspaceStore.loadTeamMembers(true);
      this.workspaceStore.loadCompanyConnectors(true);
      this.workspaceStore.loadWorkspaceSettings();
    }
  }
}
